import { ContentCopy, Download } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { clearTwoFactorError, enable2FA, setup2FA } from 'client/modules/Auth/TwoFactorActions';
import {
  getTwoFactorError,
  getTwoFactorLoading,
  getTwoFactorSetup,
} from 'client/modules/Auth/TwoFactorReducer';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const messages = defineMessages({
  step1: {
    id: 'TwoFactorSetup.Step1',
    defaultMessage: 'Step 1: Install an authenticator app',
  },
  step1Description: {
    id: 'TwoFactorSetup.Step1Description',
    defaultMessage:
      'Download and install an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.',
  },
  step2: {
    id: 'TwoFactorSetup.Step2',
    defaultMessage: 'Step 2: Scan the QR code',
  },
  step2Description: {
    id: 'TwoFactorSetup.Step2Description',
    defaultMessage: 'Open your authenticator app and scan this QR code to add your account.',
  },
  step3: {
    id: 'TwoFactorSetup.Step3',
    defaultMessage: 'Step 3: Enter verification code',
  },
  step3Description: {
    id: 'TwoFactorSetup.Step3Description',
    defaultMessage: 'Enter the 6-digit code from your authenticator app to verify the setup.',
  },
  verificationCode: {
    id: 'TwoFactorSetup.VerificationCode',
    defaultMessage: 'Verification code',
  },
  backupCodesDescription: {
    id: 'TwoFactorSetup.BackupCodesDescription',
    defaultMessage:
      'Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.',
  },
  copyBackupCodes: {
    id: 'TwoFactorSetup.CopyBackupCodes',
    defaultMessage: 'Copy backup codes',
  },
  downloadBackupCodes: {
    id: 'TwoFactorSetup.DownloadBackupCodes',
    defaultMessage: 'Download backup codes',
  },
  cancel: {
    id: 'Common.Cancel',
    defaultMessage: 'Cancel',
  },
  enable: {
    id: 'TwoFactorSetup.Enable',
    defaultMessage: 'Enable',
  },
  copySecret: {
    id: 'TwoFactorSetup.CopySecret',
    defaultMessage: 'Copy secret key',
  },
  manualEntry: {
    id: 'TwoFactorSetup.ManualEntry',
    defaultMessage: "Can't scan the QR code? Enter this secret key manually:",
  },
  next: {
    id: 'Common.Next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'Common.Back',
    defaultMessage: 'Back',
  },
  finish: {
    id: 'Common.Finish',
    defaultMessage: 'Finish',
  },
  success: {
    id: 'TwoFactorSetup.Success',
    defaultMessage: 'Success!',
  },
  cantScanQRCode: {
    id: 'TwoFactorSetup.CantScanQRCode',
    defaultMessage: "Can't scan the QR code?",
  },
  setupTitle: {
    id: 'TwoFactorSetup.SetupTitle',
    defaultMessage: 'Set Up Two-Factor Authentication',
  },
});

function TwoFactorSetup({ onComplete, onError, open, onClose }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const setup = useSelector(getTwoFactorSetup);
  const error = useSelector(getTwoFactorError);
  const isLoading = useSelector(getTwoFactorLoading);

  const [verificationCode, setVerificationCode] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [setupComplete, setSetupComplete] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Always controlled mode now
  const isOpen = open;

  useEffect(() => {
    // Start setup when dialog opens
    if (isOpen) {
      handleSetupOpen();
    }
  }, [handleSetupOpen, isOpen]);

  const handleSetupOpen = useCallback(async () => {
    try {
      await dispatch(setup2FA());
      setActiveStep(0);
      setSetupComplete(false);
    } catch (err) {
      if (onError) {
        onError(err);
      }
    }
  }, [dispatch, onError]);

  const handleSetupClose = useCallback(() => {
    if (onClose) {
      onClose();
    }

    setVerificationCode('');
    setActiveStep(0);
    setSetupComplete(false);
    setShowSecret(false);
    setCopiedSecret(false);
    dispatch(clearTwoFactorError());
  }, [dispatch, onClose]);

  const handleNext = useCallback(() => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }, []);

  const handleEnable = useCallback(
    async event => {
      try {
        event.preventDefault();
        await dispatch(enable2FA(verificationCode));
        setSetupComplete(true);
        setActiveStep(3); // Move to backup codes step
      } catch (_err) {
        // Error is handled by reducer
      }
    },
    [dispatch, verificationCode],
  );

  const copyToClipboard = useCallback((text, setCopiedState) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedState(true);
      setTimeout(() => setCopiedState(false), 2000);
    });
  }, []);

  const downloadBackupCodes = useCallback(() => {
    const codesText = setup.backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [setup.backupCodes]);

  const handleFinish = useCallback(() => {
    if (onComplete) {
      onComplete({ success: true });
    }

    // Close the dialog
    handleSetupClose();
  }, [onComplete, handleSetupClose]);

  return (
    <>
      {/* Setup Dialog - Stepper Approach */}
      <Dialog open={isOpen} onClose={handleSetupClose} maxWidth="md" fullWidth>
        <DialogTitle>{formatMessage(messages.setupTitle)}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Install Authenticator App */}
            <Step>
              <StepLabel>{formatMessage(messages.step1)}</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {formatMessage(messages.step1Description)}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" onClick={handleNext} sx={{ mt: 1, mr: 1 }}>
                    {formatMessage(messages.next)}
                  </Button>

                  <Button onClick={handleSetupClose} sx={{ mt: 1, mr: 1 }}>
                    {formatMessage(messages.cancel)}
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Step 2: Scan QR Code */}
            <Step>
              <StepLabel>{formatMessage(messages.step2)}</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatMessage(messages.step2Description)}
                </Typography>

                {setup.qrCode && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 2,
                      width: 'fit-content',
                    }}
                  >
                    <img
                      src={setup.qrCode}
                      alt="QR Code"
                      style={{ maxWidth: '200px', height: 'auto' }}
                    />
                    {setup.secret && (
                      <Button
                        onClick={() => setShowSecret(!showSecret)}
                        variant="text"
                        size="small"
                        sx={{ mb: 1, textTransform: 'none' }}
                      >
                        {formatMessage(messages.cantScanQRCode)}
                      </Button>
                    )}
                  </Box>
                )}

                {setup.secret && showSecret && (
                  <Box sx={{ mb: 2, width: '100%' }}>
                    <Typography variant="body2">{formatMessage(messages.manualEntry)}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={setup.secret}
                        fullWidth
                        size="small"
                        InputProps={{
                          readOnly: true,
                          style: { fontFamily: 'monospace', fontSize: '0.875rem' },
                        }}
                        sx={{ mt: 0 }}
                      />
                      <IconButton
                        onClick={() => copyToClipboard(setup.secret, setCopiedSecret)}
                        title={formatMessage(messages.copySecret)}
                      >
                        <ContentCopy />
                      </IconButton>
                    </Box>
                    {copiedSecret && (
                      <Typography variant="caption" color="success.main">
                        <FormattedMessage id="Common.Copied" defaultMessage="Copied!" />
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" onClick={handleNext} sx={{ mt: 1, mr: 1 }}>
                    {formatMessage(messages.next)}
                  </Button>
                  <Button onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                    {formatMessage(messages.back)}
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Step 3: Enter Verification Code */}
            <Step>
              <StepLabel>{formatMessage(messages.step3)}</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatMessage(messages.step3Description)}
                </Typography>

                <form onSubmit={handleEnable}>
                  <TextField
                    label={formatMessage(messages.verificationCode)}
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value)}
                    fullWidth
                    inputProps={{ maxLength: 6 }}
                    error={!!error}
                    helperText={error}
                    sx={{ mb: 2 }}
                    autoFocus
                  />

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      disabled={!verificationCode || verificationCode.length !== 6 || isLoading}
                      sx={{ mt: 1, mr: 1 }}
                      type="submit"
                    >
                      {isLoading ? <CircularProgress size={20} /> : formatMessage(messages.enable)}
                    </Button>
                    <Button onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                      {formatMessage(messages.back)}
                    </Button>
                  </Box>
                </form>
              </StepContent>
            </Step>

            {/* Step 4: Backup Codes (Success) */}
            <Step>
              <StepLabel>{formatMessage(messages.success)}</StepLabel>
              <StepContent>
                {setupComplete && (
                  <Box>
                    {setup.backupCodes && setup.backupCodes.length > 0 && (
                      <Box>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {formatMessage(messages.backupCodesDescription)}
                        </Alert>

                        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                          <Box
                            sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}
                          >
                            {setup.backupCodes.map((code, index) => (
                              <Typography
                                key={index}
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: 'white',
                                  padding: 1,
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  borderRadius: 1,
                                  textAlign: 'center',
                                }}
                              >
                                {code}
                              </Typography>
                            ))}
                          </Box>
                        </Paper>

                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                          <Button
                            onClick={() =>
                              copyToClipboard(setup.backupCodes.join('\n'), setCopiedCodes)
                            }
                            startIcon={<ContentCopy />}
                            variant="outlined"
                          >
                            {formatMessage(messages.copyBackupCodes)}
                          </Button>
                          <Button
                            onClick={downloadBackupCodes}
                            startIcon={<Download />}
                            variant="outlined"
                          >
                            {formatMessage(messages.downloadBackupCodes)}
                          </Button>
                        </Box>
                        {copiedCodes && (
                          <Typography variant="caption" color="success.main">
                            <FormattedMessage id="Common.Copied" defaultMessage="Copied!" />
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Button variant="contained" onClick={handleFinish} sx={{ mt: 2 }}>
                      {formatMessage(messages.finish)}
                    </Button>
                  </Box>
                )}
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TwoFactorSetup;
